import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CircleStop, Pause, Pencil, Play, Plus, Repeat } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { PermissionButton } from '@/client/components/admin/PermissionButton';
import { PERMISSIONS } from '@/client/config/permissions';
import { useGate } from '@/client/hooks/useGate';
import { useRecurringQuery, useUpsertRecurringMutation } from '@/client/api/business/spend';
import type { Cadence, RecurringCharge } from '@/client/types/business/spend';
import { BusinessPageShell } from '../components/BusinessPageShell';
import { Field, filterClass, inputClass, today, useSpendLookups } from '../../Accounting/components/spend-ui';
import { DeleteConfirmDialog, EmptyState, RowMenu, StatusBadge } from '../../HumanResource/components/hr-ui';
import { money, totals } from '../../HumanResource/components/comp-format';
import { formatDay } from '../../HumanResource/components/leave-format';

const PER: Record<Cadence, string> = { monthly: 'month', quarterly: 'quarter', yearly: 'year' };
const STATUS_BADGE: Record<RecurringCharge['status'], string> = {
  active: 'bg-emerald-50 text-emerald-700',
  paused: 'bg-amber-50 text-amber-800',
  ended: 'bg-gray-100 text-gray-500',
};

type Form = { vendorId: string; description: string; amount: string; currency: string; cadence: Cadence; nextChargeOn: string; categoryId: string; departmentId: string };

export default function RecurringPage() {
  const [params, setParams] = useSearchParams();
  const vendorFilter = params.get('vendor') ?? '';
  const recurringQ = useRecurringQuery();
  const lookups = useSpendLookups();
  const upsert = useUpsertRecurringMutation();
  const canManage = useGate(PERMISSIONS.accounting.manage).allowed;
  const [editing, setEditing] = useState<RecurringCharge | null | 'new'>(null);
  const [form, setForm] = useState<Form | null>(null);
  const [changing, setChanging] = useState<{ charge: RecurringCharge; to: RecurringCharge['status'] } | null>(null);

  const charges = (recurringQ.data ?? []).filter((c) => !vendorFilter || c.vendorId === vendorFilter);
  const active = charges.filter((c) => c.status === 'active');
  const perMonth = active.map((c) => ({ amount: c.amount / (c.cadence === 'monthly' ? 1 : c.cadence === 'quarterly' ? 3 : 12), currency: c.currency }));
  const in30 = new Date(Date.now() + 30 * 86_400_000).toISOString().slice(0, 10);

  useEffect(() => {
    if (editing === null) return setForm(null);
    const c = editing === 'new' ? null : editing;
    const vendor = lookups.vendors.find((v) => v.id === (c?.vendorId ?? vendorFilter));
    setForm({
      vendorId: c?.vendorId ?? vendorFilter,
      description: c?.description ?? '',
      amount: c ? String(c.amount) : '',
      currency: c?.currency ?? vendor?.defaultCurrency ?? 'USD',
      cadence: c?.cadence ?? 'monthly',
      nextChargeOn: c?.nextChargeOn ?? today(),
      categoryId: c?.categoryId ?? '',
      departmentId: c?.departmentId ?? '',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const save = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    upsert.mutate(
      {
        id: editing && editing !== 'new' ? editing.id : undefined,
        vendorId: form.vendorId,
        description: form.description,
        amount: Number(form.amount),
        currency: form.currency.trim().toUpperCase(),
        cadence: form.cadence,
        nextChargeOn: form.nextChargeOn,
        categoryId: form.categoryId || null,
        departmentId: form.departmentId || null,
      },
      {
        onSuccess: () => {
          toast.success(editing === 'new' ? 'Recurring charge added. Its bill appears as a draft on the next charge date.' : 'Updated');
          setEditing(null);
        },
        onError: (err) => toast.error((err as Error).message || 'Could not save'),
      },
    );
  };

  return (
    <BusinessPageShell
      mock={false}
      title="Recurring charges"
      description="Subscriptions and retainers. Each morning, every charge that has come due becomes a draft bill and moves to its next date."
      crumbs={[{ label: 'Spend' }, { label: 'Recurring charges' }]}
      icon={Repeat}
      actions={
        <PermissionButton code={PERMISSIONS.accounting.manage} className="gap-2" onClick={() => setEditing('new')}>
          <Plus className="h-4 w-4" /> Add recurring charge
        </PermissionButton>
      }
      stats={[
        { label: 'Active', value: String(active.length) },
        { label: 'Per month', value: totals(perMonth) },
        { label: 'Charging in the next 30 days', value: String(active.filter((c) => c.nextChargeOn <= in30).length) },
      ]}
    >
      <div className="mb-4">
        <select value={vendorFilter} onChange={(e) => setParams(e.target.value ? { vendor: e.target.value } : {})} className={filterClass}>
          <option value="">All vendors</option>
          {lookups.vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </div>

      {recurringQ.isError ? (
        <EmptyState label={`Could not load recurring charges: ${(recurringQ.error as Error)?.message ?? 'unknown error'}`} />
      ) : recurringQ.isLoading ? (
        <EmptyState label="Loading recurring charges…" />
      ) : charges.length === 0 ? (
        <EmptyState label="No recurring charges yet." />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-100 bg-white shadow-sm">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 font-medium">Charge</th>
                <th className="px-4 py-3 font-medium">Vendor</th>
                <th className="px-4 py-3 font-medium">Amount</th>
                <th className="px-4 py-3 font-medium">Next charge</th>
                <th className="px-4 py-3 font-medium">Last billed</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {charges.map((c) => (
                <tr key={c.id} className={canManage ? 'cursor-pointer hover:bg-gray-50' : ''} onClick={() => canManage && setEditing(c)}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{c.description}</p>
                    <p className="text-xs text-gray-400">
                      {[c.categoryId && lookups.categoryName(c.categoryId), c.departmentId && lookups.departmentName(c.departmentId)].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{lookups.vendorName(c.vendorId)}</td>
                  <td className="px-4 py-3 text-gray-900">
                    {money(c.amount, c.currency)} / {PER[c.cadence]}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.status === 'active' ? formatDay(c.nextChargeOn) : '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{c.lastBilledOn ? formatDay(c.lastBilledOn) : '—'}</td>
                  <td className="px-4 py-3">
                    <StatusBadge label={c.status} className={`capitalize ${STATUS_BADGE[c.status]}`} />
                  </td>
                  <td className="px-4 py-3 text-right">
                    <RowMenu
                      items={[
                        { label: 'Edit', icon: Pencil, hidden: !canManage, onSelect: () => setEditing(c) },
                        { label: 'Pause', icon: Pause, hidden: !canManage || c.status !== 'active', onSelect: () => setChanging({ charge: c, to: 'paused' }) },
                        { label: 'Resume', icon: Play, hidden: !canManage || c.status !== 'paused', onSelect: () => setChanging({ charge: c, to: 'active' }) },
                        { label: 'End', icon: CircleStop, danger: true, hidden: !canManage || c.status === 'ended', onSelect: () => setChanging({ charge: c, to: 'ended' }) },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? 'Add recurring charge' : 'Edit recurring charge'}</DialogTitle>
          </DialogHeader>
          {form && (
            <form onSubmit={save} className="space-y-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Vendor">
                  <select required value={form.vendorId} onChange={(e) => setForm({ ...form, vendorId: e.target.value })} className={inputClass}>
                    <option value="">Select a vendor</option>
                    {lookups.vendors.filter((v) => v.status === 'active' || v.id === form.vendorId).map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="What it is">
                  <input required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputClass} placeholder="Pro plan" />
                </Field>
                <Field label="Amount">
                  <input required type="number" min={0} step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputClass} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Currency">
                    <input required maxLength={3} value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputClass} />
                  </Field>
                  <Field label="Every">
                    <select value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value as Cadence })} className={inputClass}>
                      <option value="monthly">Month</option>
                      <option value="quarterly">Quarter</option>
                      <option value="yearly">Year</option>
                    </select>
                  </Field>
                </div>
                <Field label="Next charge date">
                  <input required type="date" value={form.nextChargeOn} onChange={(e) => setForm({ ...form, nextChargeOn: e.target.value })} className={inputClass} />
                </Field>
                <Field label="Category">
                  <select value={form.categoryId} onChange={(e) => setForm({ ...form, categoryId: e.target.value })} className={inputClass}>
                    <option value="">None</option>
                    {lookups.categories.filter((c) => c.status === 'active').map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Department">
                  <select value={form.departmentId} onChange={(e) => setForm({ ...form, departmentId: e.target.value })} className={inputClass}>
                    <option value="">Company-wide</option>
                    {lookups.departments.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </select>
                </Field>
              </div>
              <DialogFooter className="gap-2 sm:gap-0">
                <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={upsert.isPending}>
                  {upsert.isPending ? 'Saving…' : 'Save'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={!!changing}
        onOpenChange={(o) => !o && setChanging(null)}
        title={changing ? `${changing.to === 'paused' ? 'Pause' : changing.to === 'active' ? 'Resume' : 'End'} ${changing.charge.description}?` : ''}
        description={
          changing?.to === 'active'
            ? `Bills start again from ${changing ? formatDay(changing.charge.nextChargeOn) : ''}. Any dates missed while paused are billed too; move the next charge date first if they should not be.`
            : changing?.to === 'paused'
              ? 'No bills are made while it is paused.'
              : 'No more bills will be made. Bills already made stay.'
        }
        actionLabel={changing?.to === 'paused' ? 'Pause' : changing?.to === 'active' ? 'Resume' : 'End'}
        danger={changing?.to === 'ended'}
        onConfirm={() => {
          if (changing) upsert.mutate({ id: changing.charge.id, status: changing.to }, { onSuccess: () => toast.success('Saved'), onError: (e) => toast.error((e as Error).message) });
          setChanging(null);
        }}
      />
    </BusinessPageShell>
  );
}
